import type { ReactNode } from "react";
import { Avatar, Box, Stack, Typography } from "@mui/material";
import { useRegistry } from "../../context/RegistryContext";
import type { InvoiceEntity } from "../../api/types";
import { logoUrl } from "../../api/logo";
import { invoiceSender, senderName } from "../../logic/invoice-sender";

export type SenderHeadingSize = "sm" | "md";

export type SenderHeadingProps = {
  /** The invoice whose sender — a payee or an account — heads the card. */
  invoice: Pick<InvoiceEntity, "payeeId" | "sourceId">;
  size?: SenderHeadingSize;
  /** A line under the sender's name — plain text, or e.g. a link to the
   * invoice itself. Shown in place of the id when the sender is gone. */
  subtitle?: ReactNode;
};

const AVATAR_SIZE: Record<SenderHeadingSize, number> = {
  sm: 28,
  md: 40,
};

const TITLE_VARIANT: Record<SenderHeadingSize, "body2" | "subtitle1"> = {
  sm: "body2",
  md: "subtitle1",
};

const initial = (name: string) => name.trim().charAt(0).toUpperCase() || "?";

export const SenderHeading = ({
  invoice,
  size = "md",
  subtitle,
}: SenderHeadingProps) => {
  // Hidden senders included: hiding a payee keeps it out of pickers, but its
  // invoices still name it.
  const { withHidden } = useRegistry();
  const sender = invoiceSender(invoice, withHidden);
  const entity = sender?.entity;

  const px = AVATAR_SIZE[size];
  const titleVariant = TITLE_VARIANT[size];

  if (!sender || !entity) {
    return (
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: "center", minWidth: 0 }}
      >
        <Avatar
          variant="rounded"
          sx={{ width: px, height: px, bgcolor: "grey.300", fontSize: px / 2 }}
        >
          ?
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant={titleVariant} sx={{ fontWeight: 600 }} noWrap>
            {senderName(sender)}
          </Typography>
          {subtitle ? (
            <Typography
              variant="caption"
              component="div"
              sx={{ color: "text.secondary" }}
              noWrap
            >
              {subtitle}
            </Typography>
          ) : (
            sender && (
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontFamily: "monospace" }}
                noWrap
              >
                {sender.id}
              </Typography>
            )
          )}
        </Box>
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{ alignItems: "center", minWidth: 0 }}
    >
      <Avatar
        variant="rounded"
        alt={entity.name}
        src={logoUrl(sender.kind, entity)}
        sx={{
          width: px,
          height: px,
          bgcolor: entity.colorBg || undefined,
          color: entity.colorFg || undefined,
          fontSize: px / 2,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        {initial(entity.name)}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant={titleVariant}
          sx={{
            fontWeight: 600,
            fontStyle: entity.style === "italic" ? "italic" : undefined,
          }}
          noWrap
          title={entity.name}
        >
          {entity.name}
        </Typography>
        {subtitle && (
          <Typography
            variant="caption"
            component="div"
            sx={{ color: "text.secondary" }}
            noWrap
            title={typeof subtitle === "string" ? subtitle : undefined}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
  );
};
